/**
 * English-translation leakage detector.
 *
 * South African indigenous languages (isiZulu, isiXhosa, …) run on Gemini in
 * beta. The one validated failure mode (DTP-005 2026-06-24/26) is that Gemini
 * sometimes *translates* the audio to English instead of transcribing it
 * verbatim — handing the user English text under an in-language label.
 *
 * This pure heuristic flags that case so we can warn the user. The signal is
 * **English function-word density**, NOT just "contains English words":
 *
 *   - Function words (the, of, and, is, …) encode English *grammar*. A full
 *     English translation is dense with them (~30–50% of tokens).
 *   - South African code-switching keeps the *Nguni/Sotho grammar* and swaps
 *     only content words ("ngiya-e-meeting ne-laptop"), so English function-word
 *     density stays near zero. That makes this robust to legitimate
 *     code-switching — we only flag near-total English output.
 *
 * No audio or network needed — fully unit-testable.
 */

// Closed-class English function words: frequent in English prose, essentially
// absent from isiZulu/isiXhosa/Sesotho/Setswana/Tshivenda orthography.
const ENGLISH_FUNCTION_WORDS = new Set([
  'the', 'of', 'and', 'to', 'a', 'in', 'is', 'was', 'are', 'for', 'that', 'it',
  'with', 'as', 'his', 'on', 'be', 'at', 'by', 'this', 'had', 'not', 'but',
  'from', 'or', 'have', 'an', 'they', 'which', 'you', 'were', 'her', 'all',
  'she', 'there', 'would', 'their', 'we', 'him', 'been', 'has', 'when', 'who',
  'will', 'more', 'no', 'if', 'out', 'so', 'what', 'up', 'can', 'about', 'into',
  'than', 'them', 'only', 'some', 'could', 'then', 'these', 'i', 'he', 'do',
  'does', 'did', 'our', 'your', 'its', 'how', 'why', 'where',
]);

export interface LeakageResult {
  /** True when the text looks like a near-complete English translation. */
  flagged: boolean;
  /** Fraction of tokens that are English function words (0–1). */
  englishRatio: number;
  /** Tokens considered (after stripping timestamps/speaker labels). */
  wordCount: number;
}

// Flag only when the text is overwhelmingly English-grammatical. Pure English
// prose runs ~0.30–0.50; isiZulu (even heavily code-switched) sits near 0.
const ENGLISH_RATIO_THRESHOLD = 0.20;
// Don't judge tiny fragments — too little signal, high false-positive risk.
const MIN_WORDS = 8;

/**
 * Heuristically decide whether `transcript` is an English translation rather
 * than an in-language transcription. Conservative by design: it should fire
 * only on near-total English, never on normal code-switched local speech.
 */
export function looksLikeEnglishTranslation(transcript: string): LeakageResult {
  const text = (transcript || '')
    // Strip "[MM:SS]" timestamps and "**Speaker N:**" labels so they don't skew the ratio.
    .replace(/\[\d{1,2}:\d{2}(?::\d{2})?\]/g, ' ')
    .replace(/\*\*[^*]+\*\*/g, ' ')
    .toLowerCase();

  const words = text.match(/[a-z']+/g) ?? [];
  const wordCount = words.length;
  if (wordCount < MIN_WORDS) {
    return { flagged: false, englishRatio: 0, wordCount };
  }

  let fn = 0;
  for (const w of words) if (ENGLISH_FUNCTION_WORDS.has(w)) fn++;
  const englishRatio = fn / wordCount;

  return {
    flagged: englishRatio >= ENGLISH_RATIO_THRESHOLD,
    englishRatio,
    wordCount,
  };
}
